import { PermissionSubdomain } from './permission-subdomain';
import { grantThisRolePermission, Permissions } from './permissions';

export type RolePermissionMapping = { [roleCode: string]: string[] };

export type RoleDomainSubdomain = {
  role: string;
  domain: string;
  subdomain?: string;
};

/**
 * Given a role-permission mapping, generate a set of <permission, subdomain> pairs
 * that apply at the given tenant.
 *
 * This essentially needs to invert the role-permission mapping, then traverse the user
 * roles to find matching permissions, and subdomains if present on the role.
 *
 * @param  {string} tenantDomain - the IDP shortcode for a tenant
 * @param  {RolePermissionMapping} roleMapping - the role-permission mapping
 * @param  {string[]} userOAuthScopes - a list of user OAuth scopes
 * @return {Set<PermissionSubdomain>} - a set of <permission, subdomain> pairs.
 */
export function createUserPermissionsSet(
  tenantDomain: string,
  roleMapping: RolePermissionMapping,
  userOAuthScopes: string[],
): Set<PermissionSubdomain> {
  // add all `PermissionSubdomain`s to a Set
  const reduceAddAllToSet = (
    permissionsSet: Set<PermissionSubdomain>,
    permission: PermissionSubdomain,
  ) => permissionsSet.add(permission);

  // flatten an iterable of `PermissionSubdomain[]`s into a single Set
  const reducePermissionArraysIntoASet = (
    permissionSet: Set<PermissionSubdomain>,
    permissions: PermissionSubdomain[],
  ) => permissions.reduce(reduceAddAllToSet, permissionSet);

  // foreach user role...
  return (
    userOAuthScopes
      // ...extract OAuth scope into <roleCode, IdpShortCode, subdomain> triples
      .map(getRoleDomainSubdomain)
      // ...select only roles with the correct IDP shortcode
      .filter(
        roleDomainSub => roleDomainSub && roleDomainSub.domain === tenantDomain,
      )
      // ...convert to <permission, subdomain> pairs
      .map(makePermissionSubdomainConvertor(roleMapping))
      // ...flatten <permission, subdomain> pairs into a single set
      .reduce(reducePermissionArraysIntoASet, new Set<PermissionSubdomain>())
  );
}

/**
 * Convert an OAuth scope into a <role, domain, subdomain> triple, if possible.
 *
 * NOTE: this will only return a defined result if the OAuth scope contains either:
 * - A role and domain
 * - A role, domain, and subdomain
 *
 * All other OAuth scopes will generate undefined values.
 *
 * @param  {string}              oauthScope - an OAuth scope
 * @return {?RoleDomainSubdomain} a <role, domain, subdomain> triple, if available.
 */
export function getRoleDomainSubdomain(
  oauthScope: string,
): RoleDomainSubdomain {
  const roleOAuthScopeMatch =
    oauthScope && oauthScope.match(/^([^@]+)@([^:@]+)(:([^@]+))?$/);

  // non-matching OAuth scopes are omitted
  if (!roleOAuthScopeMatch) {
    return undefined;
  }

  // extract the key components and return
  const [, role, domain, , subdomain] = roleOAuthScopeMatch;

  return { role, domain, subdomain };
}

type PermissionSubdomainConvertor = (
  role: RoleDomainSubdomain,
) => PermissionSubdomain[];

/**
 * Creates a function that can extract all <permission, subdomain> pairs from a mapping,
 * for a given <role, domain, subdomain> triple.
 *
 * @param {RoleDomainSubdomain} roleMapping - global role-permission mappings
 * @returns (RoleDomainSubdomain) => PermissionSubdomain[] - a function for extracting <permission, subdomain> pairs
 */
export function makePermissionSubdomainConvertor(
  roleMapping: RolePermissionMapping,
): PermissionSubdomainConvertor {
  return (roleDomainSubdomain: RoleDomainSubdomain) => {
    const { role, subdomain } = roleDomainSubdomain;
    return (roleMapping[role] || []).map((permission: string) => ({
      permission: convertGrantThisRolePermission(permission, role),
      subdomain,
    }));
  };
}

/**
 * Convert a permission to an unambiguous permission string.
 * Most permissions remain unchanged, but since a GRANT_THIS_ROLE permission must be related to a specific role to make
 * sense, we need to modify the string to include the role it refers to.
 * @param {string} permission - The permission to convert
 * @param {string} role - The role this permission is mapped from
 * @returns {string} - The modified permission string
 */
function convertGrantThisRolePermission(
  permission: string,
  role: string,
): string {
  if (permission === Permissions.GRANT_THIS_ROLE) {
    return grantThisRolePermission(role);
  }
  return permission;
}
